import React, { useEffect, useRef } from 'react';

import {
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import PaymentMethodSelect from 'components/form-fields/auto-complete-fields/PaymentMethodSelect';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useLateCancelAthleteAppointment from 'hooks/athleteAppointment/useLateCancelAthleteAppointment';
import useNoShowAthleteAppointment from 'hooks/athleteAppointment/useNoShowAthleteAppointment';
import useLateCancelAthleteClass from 'hooks/athleteClass/useLateCancelAthleteClass';
import useNoShowAthleteClass from 'hooks/athleteClass/useNoShowAthleteClass';
import useAthletePackageAllLimitedCreditsUsed from 'hooks/athletePackage/useAthletePackageAllLimitedCreditsUsed';
import useLastCreditAlertBuilder from 'hooks/athletePackage/useLastCreditAlertBuilder';
import useGym from 'hooks/gym/useGym';
import useAthleteUpcomingInvoices from 'hooks/invoice/useAthleteUpcomingInvoices';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { NonStripePaymentMethod } from 'types/enums';
import { LastCreditAlertContent } from 'utils/last-credit-alert';
import notificationHandler from 'utils/notifications';

interface CancellationSettings {
  cancellationFee: number;
  cancellationCreditDeduction: boolean;
  noShowFee: number;
  noShowCreditDeduction: boolean;
}

interface ModalLateCancelNoShowProps {
  opened: boolean;
  isLateCancel: boolean;
  eventType: 'class' | 'appointment';
  athleteEventId: string;
  athleteId: string;
  athleteName: string;
  eventId: string;
  eventName: string;
  gymId?: string;
  cancellationSettings: CancellationSettings | undefined;
  handleClose: () => void;
  triggeredBy: 'kiosk' | 'calendar' | 'attendance';
  setLastCreditAlert?: React.Dispatch<React.SetStateAction<LastCreditAlertContent | null>>;
}

const COLLECTION_OPTIONS = {
  NOW: 'Now',
  FUTURE_DATE: 'Future Date',
  ADD_TO_NEXT_INVOICE: 'Add to next invoice',
} as const;

type ChargeTimeline = (typeof COLLECTION_OPTIONS)[keyof typeof COLLECTION_OPTIONS];

interface FormValues {
  chargeFeeSwitchOn: boolean;
  chargeDate: Date;
  chargeFee: number;
  taxRate: number;
  chargeTimeline: ChargeTimeline;
  autoPay: 'True' | 'False';
  paymentMethod: string;
  nonStripePaymentMethod: NonStripePaymentMethod | null;
  deductCredit: boolean;
}

const INPUT_WIDTH = 160;
const LABEL_MIN_WIDTH = 160;
const minDueDate = dayjs().add(1, 'day').toDate();

const ModalLateCancelNoShow = ({
  opened,
  isLateCancel,
  eventType,
  athleteEventId,
  athleteId,
  athleteName,
  eventId,
  eventName,
  gymId,
  cancellationSettings,
  handleClose,
  triggeredBy,
  setLastCreditAlert,
}: ModalLateCancelNoShowProps): JSX.Element => {
  const { dateFormat } = useLocalization();
  const staffTracking = useStaffTracking();
  const { trackLateCancelNoShowModalOpened } = staffTracking;
  const openedAtRef = useRef<number | null>(null);

  const lateCancelAthleteClass = useLateCancelAthleteClass();
  const noShowAthleteClass = useNoShowAthleteClass();
  const lateCancelAthleteAppointment = useLateCancelAthleteAppointment();
  const noShowAthleteAppointment = useNoShowAthleteAppointment();
  const getAthletePackageAllLimitedCreditsUsed = useAthletePackageAllLimitedCreditsUsed();
  const buildLastCreditAlert = useLastCreditAlertBuilder('staff');
  const { data: gymData } = useGym(gymId);

  const gymTaxRatePercent = gymData ? parseFloat(gymData.data.lateCancelNoShowTaxRate) * 100 : null;

  const getDefaultFee = (): number => {
    if (!cancellationSettings) return 0;
    return isLateCancel ? cancellationSettings.cancellationFee : cancellationSettings.noShowFee;
  };

  const getDefaultDeductCredit = (): boolean => {
    if (!cancellationSettings) return false;
    return isLateCancel
      ? cancellationSettings.cancellationCreditDeduction
      : cancellationSettings.noShowCreditDeduction;
  };

  const form = useForm<FormValues>({
    initialValues: {
      chargeFeeSwitchOn: getDefaultFee() > 0,
      chargeDate: new Date(),
      chargeFee: getDefaultFee(),
      taxRate: 0,
      chargeTimeline: COLLECTION_OPTIONS.NOW,
      autoPay: 'False',
      paymentMethod: '',
      nonStripePaymentMethod: null,
      deductCredit: getDefaultDeductCredit(),
    },
  });

  const { hasUpcomingInvoices } = useAthleteUpcomingInvoices(
    opened && form.values.chargeFeeSwitchOn ? athleteId : undefined,
  );

  const availableCollectionOptions = hasUpcomingInvoices
    ? Object.values(COLLECTION_OPTIONS)
    : Object.values(COLLECTION_OPTIONS).filter(o => o !== COLLECTION_OPTIONS.ADD_TO_NEXT_INVOICE);

  const handleChargeTimelineChange = (value: ChargeTimeline): void => {
    form.setFieldValue('chargeTimeline', value);
    form.setFieldValue('chargeDate', value === COLLECTION_OPTIONS.NOW ? new Date() : minDueDate);
  };

  const handlePaymentChange = (value: string): void => {
    form.setFieldValue('paymentMethod', value);
    if (Object.values(NonStripePaymentMethod).includes(value as NonStripePaymentMethod)) {
      form.setFieldValue('nonStripePaymentMethod', value as NonStripePaymentMethod);
      form.setFieldValue('autoPay', 'False');
    } else {
      form.setFieldValue('nonStripePaymentMethod', null);
    }
  };

  const handleChargeFeeSwitchChange = (): void => {
    form.setFieldValue('chargeFeeSwitchOn', !form.values.chargeFeeSwitchOn);
  };

  useEffect(() => {
    if (!opened) return;
    openedAtRef.current = Date.now();
    trackLateCancelNoShowModalOpened(
      athleteId,
      athleteName,
      eventId,
      eventName,
      eventType,
      isLateCancel ? 'late cancel' : 'no show',
      triggeredBy,
    );
  }, [
    opened,
    athleteId,
    athleteName,
    eventId,
    eventName,
    eventType,
    isLateCancel,
    trackLateCancelNoShowModalOpened,
    triggeredBy,
  ]);

  const getTimeOpenSeconds = (): number => {
    if (!openedAtRef.current) return 0;
    return Math.round((Date.now() - openedAtRef.current) / 1000);
  };

  const closeHandler = (): void => {
    staffTracking.trackLateCancelNoShowModalDismissed(
      athleteId,
      eventId,
      eventType,
      isLateCancel ? 'late cancel' : 'no show',
      getTimeOpenSeconds(),
    );
    form.reset();
    handleClose();
  };

  const { successNotification, errorNotification } = notificationHandler();

  const handleMutationSuccess = (response: { data?: { creditDispensation?: unknown } }): void => {
    successNotification('Athlete reservation update success', 'Reservation successfully updated.');
    staffTracking.trackAthleteLateCancelNoShow(
      athleteId,
      athleteName,
      eventId,
      eventName,
      eventType,
      isLateCancel ? 'late cancel' : 'no show',
      triggeredBy,
      !form.values.chargeFeeSwitchOn,
      form.values.chargeTimeline === COLLECTION_OPTIONS.NOW,
      form.values.deductCredit,
      form.values.chargeFee,
      getTimeOpenSeconds(),
    );
    if (setLastCreditAlert && response.data?.creditDispensation) {
      getAthletePackageAllLimitedCreditsUsed.mutate(
        { creditDispensation: response.data.creditDispensation as string },
        {
          onSuccess: res => {
            const alert = buildLastCreditAlert(res.data, athleteName.split(' ')[0]);
            if (alert) {
              setLastCreditAlert(alert);
            }
          },
          onError: error => {
            errorNotification('Error to check if all limited credits used', error.response?.data.message);
          },
        },
      );
    }
    form.reset();
    handleClose();
  };

  const handleMutationError = (error: { response?: { data?: { message?: string } } }): void => {
    errorNotification('Athlete reservation update failed', error.response?.data?.message);
  };

  const handleUpdate = (): void => {
    const basePayload = {
      chargeFee: form.values.chargeFeeSwitchOn ? form.values.chargeFee : 0,
      deductCredit: form.values.deductCredit,
      taxRate: form.values.chargeFeeSwitchOn ? form.values.taxRate : null,
      chargeNow: form.values.chargeTimeline === COLLECTION_OPTIONS.NOW,
      chargeDate: dayjs(form.values.chargeDate).format('YYYY-MM-DD'),
      autoPay:
        form.values.chargeTimeline === COLLECTION_OPTIONS.FUTURE_DATE && form.values.autoPay === 'True',
      addToNextInvoice: form.values.chargeTimeline === COLLECTION_OPTIONS.ADD_TO_NEXT_INVOICE,
      paymentMethod: form.values.nonStripePaymentMethod ? null : form.values.paymentMethod || null,
      nonStripePaymentMethod: form.values.nonStripePaymentMethod,
    };
    if (eventType === 'class') {
      const mutation = isLateCancel ? lateCancelAthleteClass : noShowAthleteClass;
      mutation.mutate(
        { athleteKiloClassId: athleteEventId, ...basePayload },
        { onSuccess: handleMutationSuccess, onError: handleMutationError },
      );
    } else {
      const mutation = isLateCancel ? lateCancelAthleteAppointment : noShowAthleteAppointment;
      mutation.mutate(
        { athleteAppointmentId: athleteEventId, ...basePayload },
        { onSuccess: handleMutationSuccess, onError: handleMutationError },
      );
    }
  };

  useShallowEffect(() => {
    const defaultFee = getDefaultFee();
    form.setFieldValue('chargeFee', defaultFee);
    form.setFieldValue('chargeFeeSwitchOn', defaultFee > 0);
    form.setFieldValue('deductCredit', getDefaultDeductCredit());
    if (gymTaxRatePercent !== null) {
      form.setFieldValue('taxRate', gymTaxRatePercent);
    }
  }, [isLateCancel, cancellationSettings, opened, gymTaxRatePercent]);

  const getIsLoading = (): boolean => {
    if (eventType === 'class') {
      return isLateCancel ? lateCancelAthleteClass.isLoading : noShowAthleteClass.isLoading;
    }
    return isLateCancel ? lateCancelAthleteAppointment.isLoading : noShowAthleteAppointment.isLoading;
  };
  const isLoading = getIsLoading();

  return (
    <Modal
      centered
      opened={opened}
      onClose={closeHandler}
      title={<Title order={4}>{isLateCancel ? 'Mark Late Cancelled' : 'Mark No Show'}</Title>}
      size='md'
    >
      <Stack>
        <Group position='apart' align='flex-start'>
          <Stack spacing={2}>
            <Text weight={500}>Charge Fee</Text>
            <Text size='sm' color='dimmed'>
              {isLateCancel ? 'Charge a late cancellation fee' : 'Charge a no-show fee'}
            </Text>
          </Stack>
          <Switch
            checked={form.values.chargeFeeSwitchOn}
            onChange={handleChargeFeeSwitchChange}
            color='kiloBlue'
          />
        </Group>

        {form.values.chargeFeeSwitchOn && (
          <Stack spacing={16} pt={16}>
            <Group spacing={16} noWrap align='center'>
              <Text size='sm' weight={500} style={{ minWidth: LABEL_MIN_WIDTH, flexShrink: 0 }}>
                Fee Amount
              </Text>
              <NumberInput
                {...form.getInputProps('chargeFee')}
                precision={2}
                step={1}
                min={0}
                icon='$'
                size='sm'
                style={{ width: INPUT_WIDTH, flexShrink: 0 }}
              />
            </Group>
            <Group spacing={16} noWrap align='center'>
              <Text size='sm' weight={500} style={{ minWidth: LABEL_MIN_WIDTH, flexShrink: 0 }}>
                Cancellation Tax Rate
              </Text>
              <NumberInput
                {...form.getInputProps('taxRate')}
                precision={3}
                step={1}
                min={0}
                max={100}
                rightSection={
                  <Text size='sm' color='dimmed'>
                    %
                  </Text>
                }
                size='sm'
                style={{ width: INPUT_WIDTH, flexShrink: 0 }}
              />
            </Group>
            <Group spacing={16} noWrap align='center'>
              <Text size='sm' weight={500} style={{ minWidth: LABEL_MIN_WIDTH, flexShrink: 0 }}>
                Collection Date
              </Text>
              <Select
                {...form.getInputProps('chargeTimeline')}
                data={availableCollectionOptions}
                onChange={handleChargeTimelineChange}
                size='sm'
                style={{ width: INPUT_WIDTH, flexShrink: 0 }}
              />
            </Group>
            {form.values.chargeTimeline === COLLECTION_OPTIONS.FUTURE_DATE && (
              <Group spacing={16} noWrap align='center'>
                <Text size='sm' weight={500} style={{ minWidth: LABEL_MIN_WIDTH, flexShrink: 0 }}>
                  Due Date
                </Text>
                <DatePickerInput
                  {...form.getInputProps('chargeDate')}
                  minDate={minDueDate}
                  valueFormat={dateFormat}
                  size='sm'
                  style={{ width: INPUT_WIDTH, flexShrink: 0 }}
                  dropdownType='modal'
                  modalProps={{ centered: true }}
                />
              </Group>
            )}
            {form.values.chargeTimeline !== COLLECTION_OPTIONS.ADD_TO_NEXT_INVOICE && (
              <>
                {form.values.chargeTimeline === COLLECTION_OPTIONS.FUTURE_DATE && (
                  <Group spacing={16} noWrap align='center'>
                    <Text size='sm' weight={500} style={{ minWidth: LABEL_MIN_WIDTH, flexShrink: 0 }}>
                      AutoPay
                    </Text>
                    <Select
                      {...form.getInputProps('autoPay')}
                      data={[
                        { value: 'True', label: 'On' },
                        { value: 'False', label: 'Off' },
                      ]}
                      disabled={form.values.nonStripePaymentMethod !== null}
                      size='sm'
                      style={{ width: INPUT_WIDTH, flexShrink: 0 }}
                    />
                  </Group>
                )}
                <Group spacing={16} noWrap align='center'>
                  <Text size='sm' weight={500} style={{ minWidth: LABEL_MIN_WIDTH, flexShrink: 0 }}>
                    Payment Method
                  </Text>
                  <PaymentMethodSelect
                    athleteId={athleteId}
                    value={form.values.paymentMethod}
                    onChange={handlePaymentChange}
                    allowCash
                    useDefaultPayment
                    size='sm'
                    style={{ width: INPUT_WIDTH, flexShrink: 0 }}
                  />
                </Group>
              </>
            )}
          </Stack>
        )}

        <Divider />

        <Group position='apart' align='flex-start'>
          <Stack spacing={2}>
            <Text weight={500}>Deduct Credit</Text>
            <Text size='sm' color='dimmed'>
              Remove session credit
            </Text>
          </Stack>
          <Switch
            checked={form.values.deductCredit}
            onChange={() => form.setFieldValue('deductCredit', !form.values.deductCredit)}
            color='kiloBlue'
          />
        </Group>

        <Group position='right'>
          <Button variant='default' onClick={closeHandler}>
            Cancel
          </Button>
          <Button onClick={handleUpdate} color='kiloBlue' loading={isLoading}>
            Confirm
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalLateCancelNoShow;
